import React, { useState, useCallback } from 'react';
import Button from '../../ui/Button';
import { WIZARD_STEPS } from './constants';
import SourceSelectStep from './steps/SourceSelectStep';
import MethodSelectStep from './steps/MethodSelectStep';
import ConnectionStep from './steps/ConnectionStep';
import CsvUploadStep from './steps/CsvUploadStep';
import DomainMappingStep from './steps/DomainMappingStep';
import UserSelectStep from './steps/UserSelectStep';
import ValidationStep from './steps/ValidationStep';
import ReviewStep from './steps/ReviewStep';

const initialState = {
  source: null,
  method: null, // 'individual' | 'bulk'
  credentials: {},
  connectionResult: null,
  users: [],
  selectedUsers: [],
  selectedUserDetails: [],
  domainMapping: {},
  csvData: null,
  csvUsers: [],
  validationResult: null,
  enumNotSupported: false
};

export default function MigrationWizard({ onClose, onStarted, showNotification }) {
  const [state, setState] = useState(initialState);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const steps = WIZARD_STEPS[state.method] || WIZARD_STEPS.individual;
  const currentStep = steps[currentStepIndex];

  const updateState = useCallback((updates) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  const goNext = useCallback(() => {
    setCurrentStepIndex(prev => Math.min(prev + 1, steps.length - 1));
  }, [steps.length]);

  const goBack = useCallback(() => {
    setCurrentStepIndex(prev => Math.max(prev - 1, 0));
  }, []);

  const goToStep = useCallback((index) => {
    if (index < currentStepIndex) {
      setCurrentStepIndex(index);
    }
  }, [currentStepIndex]);

  const handleMethodChange = useCallback((method) => {
    updateState({ method });
    const nextStepId = method === 'bulk' ? 'csv' : 'connection';
    const targetSteps = WIZARD_STEPS[method] || WIZARD_STEPS.individual;
    const idx = targetSteps.findIndex(s => s.id === nextStepId);
    setCurrentStepIndex(idx > -1 ? idx : 2);
  }, [updateState]);

  const renderStep = () => {
    if (!currentStep) return null;

    switch (currentStep.id) {
      case 'source':
        return (
          <SourceSelectStep
            selected={state.source}
            onSelect={(source) => { updateState({ source }); goNext(); }}
          />
        );
      case 'method':
        return (
          <MethodSelectStep
            selected={state.method}
            source={state.source}
            onSelect={handleMethodChange}
          />
        );
      case 'connection':
        return (
          <ConnectionStep
            source={state.source}
            credentials={state.credentials}
            onUpdate={(creds, result) => {
              updateState({
                credentials: creds,
                connectionResult: result,
                users: result?.users || [],
                enumNotSupported: result?._enumNotSupported || false
              });
            }}
            onNext={goNext}
            showNotification={showNotification}
          />
        );
      case 'csv':
        return (
          <CsvUploadStep
            csvData={state.csvData}
            csvUsers={state.csvUsers}
            onUpdate={(csvData, csvUsers) => updateState({ csvData, csvUsers })}
            onNext={goNext}
          />
        );
      case 'domains':
        return (
          <DomainMappingStep
            users={state.users}
            domainMapping={state.domainMapping}
            onUpdate={(mapping) => updateState({ domainMapping: mapping })}
            onNext={goNext}
          />
        );
      case 'users':
        return (
          <UserSelectStep
            users={state.users}
            selectedUsers={state.selectedUsers}
            enumNotSupported={state.enumNotSupported}
            onUpdate={(selected, details) => updateState({ selectedUsers: selected, selectedUserDetails: details })}
            onNext={goNext}
          />
        );
      case 'validation':
        return (
          <ValidationStep
            method={state.method}
            source={state.source}
            credentials={state.credentials}
            selectedUsers={state.selectedUsers}
            csvUsers={state.csvUsers}
            onUpdate={(result) => updateState({ validationResult: result })}
            onNext={goNext}
            showNotification={showNotification}
          />
        );
      case 'review':
        return (
          <ReviewStep
            state={state}
            onStarted={onStarted}
            showNotification={showNotification}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Stepper */}
      <div className="flex items-center gap-2 px-2 py-4 border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
        {steps.map((step, index) => (
          <div key={step.id} className="flex items-center">
            <button
              onClick={() => goToStep(index)}
              disabled={index > currentStepIndex}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                index === currentStepIndex
                  ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                  : index < currentStepIndex
                  ? 'text-emerald-600 dark:text-emerald-400 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer'
                  : 'text-gray-400 cursor-not-allowed'
              }`}
            >
              <span className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                index === currentStepIndex
                  ? 'bg-orange-500 text-white'
                  : index < currentStepIndex
                  ? 'bg-emerald-500 text-white'
                  : 'bg-gray-200 text-gray-500 dark:bg-gray-700'
              }`}>
                {index < currentStepIndex ? (
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  index + 1
                )}
              </span>
              <span className="hidden sm:inline">{step.label}</span>
            </button>
            {index < steps.length - 1 && (
              <svg className="w-4 h-4 text-gray-300 mx-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            )}
          </div>
        ))}
      </div>

      {/* Step Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {renderStep()}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
        <Button
          variant="ghost"
          onClick={currentStepIndex === 0 ? onClose : goBack}
        >
          {currentStepIndex === 0 ? 'Cancel' : 'Back'}
        </Button>
        <span className="text-xs text-gray-400">
          Step {currentStepIndex + 1} of {steps.length}
        </span>
      </div>
    </div>
  );
}
